import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
  LayoutDashboard,
  FileText,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  Shield,
  Receipt,
  ClipboardList
} from 'lucide-react';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Filter menu items based on user role
  const allMenuItems = [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
    { name: 'Introducción de Datos', icon: FileText, path: '/entrada-datos' },
    { name: 'Facturación', icon: Receipt, path: '/facturacion' },
    { name: 'Informes', icon: ClipboardList, path: '/informes' },
    { name: 'Estadísticas', icon: BarChart3, path: '/estadisticas' },
    { name: 'Auditoría', icon: Shield, path: '/auditoria', superuserOnly: true },
    { name: 'Configuración', icon: Settings, path: '/configuracion', superuserOnly: true },
  ];

  const menuItems = allMenuItems.filter(item => !item.superuserOnly || user?.is_superuser);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex" data-testid="layout-container">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 fixed inset-y-0 left-0 z-50 w-64 bg-gradient-to-b from-blue-900 to-purple-900 text-white transition-transform duration-300 flex flex-col shadow-2xl`}
        data-testid="sidebar"
      >
        {/* Header */}
        <div className="p-4 flex items-center justify-between border-b border-blue-700">
          <div className="flex items-center gap-3 flex-1">
            <div className="bg-white p-2 rounded-lg">
              <img 
                src="/imagotipo.png" 
                alt="Logo" 
                className="h-10 w-auto object-contain"
                data-testid="sidebar-logo"
              />
            </div>
            <h1 className="text-lg font-bold bg-gradient-to-r from-blue-200 to-purple-200 bg-clip-text text-transparent">
              CRM Externos
            </h1>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            data-testid="sidebar-close"
            className="p-2 rounded-lg hover:bg-blue-800 transition-colors lg:hidden"
          >
            <X size={20} />
          </button>
        </div>

        {/* Menu Items */}
        <nav className="flex-1 p-4 space-y-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  setSidebarOpen(false);
                }}
                data-testid={`menu-${item.path}`}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                  isActive
                    ? 'bg-white text-blue-900 shadow-lg font-semibold'
                    : 'hover:bg-blue-800 text-white'
                }`}
              >
                <Icon size={20} />
                <span>{item.name}</span>
              </button>
            );
          })}
        </nav>

        {/* User Info - Moved to bottom */}
        {user && (
          <div className="mt-auto p-4 border-t border-blue-700 bg-blue-800/50">
            <div>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="font-semibold text-sm">{user.full_name}</p>
                  <p className="text-xs text-blue-200 mt-1">{user.email}</p>
                </div>
              </div>
              {user.is_superuser && (
                <span className="inline-block mt-2 px-2 py-1 text-xs bg-yellow-500 text-yellow-900 rounded-full font-semibold">
                  Superusuario
                </span>
              )}
              <button
                onClick={handleLogout}
                data-testid="logout-button"
                className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors font-medium"
                title="Cerrar sesión"
              >
                <LogOut size={18} />
                <span>Cerrar sesión</span>
              </button>
            </div>
          </div>
        )}
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 min-h-screen overflow-y-auto">
        {/* Mobile Header with Hamburger */}
        <div className="lg:hidden bg-white shadow-md p-4 flex items-center justify-between sticky top-0 z-30">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            data-testid="mobile-menu-button"
          >
            <Menu size={24} className="text-gray-700" />
          </button>
          <h1 className="text-lg font-bold text-gray-800">Sistema ANMAR</h1>
          <div className="w-10" />
        </div>

        <div className="p-4 sm:p-6 lg:p-8">{children}</div>
      </main>
    </div>
  );
};

export default Layout;
